import React, { useState, useRef, useEffect } from 'react';
import AdminLogin from './AdminLogin';
import ProductManager from './ProductManager';
import CategoryManager from './CategoryManager';
import { ProductProps } from '../../components/ui/ProductCard';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import LargeLogo from '../../assets/images/Logo Large.png';
import { authAPI } from '../../services/api';

// Sample product data - in a real app, this would come from a database
import { productsData } from '../../data/products';

const AdminPanel: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState<'products' | 'categories'>('products');
  const [products, setProducts] = useState<ProductProps[]>(productsData);
  const [isLoading, setIsLoading] = useState(true);
  const parallaxRef = useRef<HTMLDivElement>(null);
  
  // Check authentication status on component mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        await authAPI.checkStatus();
        setIsLoggedIn(true);
      } catch (error) {
        setIsLoggedIn(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuthStatus();
  }, []);
  
  // Parallax effect for the background
  useEffect(() => {
    if (!isLoggedIn) {
      const handleMouseMove = (e: MouseEvent) => {
        if (!parallaxRef.current) return;
        
        const x = (window.innerWidth - e.pageX * 2) / 100;
        const y = (window.innerHeight - e.pageY * 2) / 100;
        
        parallaxRef.current.style.transform = `translateX(${x}px) translateY(${y}px)`;
      };
      
      document.addEventListener('mousemove', handleMouseMove);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [isLoggedIn]);

  const handleLogin = (username: string, password: string) => {
    // API call is made in the AdminLogin component
    // We just need to update the state here
    setIsLoggedIn(true);
    return true;
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggedIn(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header isAdminPage={true} />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center p-10">
            <div className="inline-block w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
        <Footer isAdminPage={true} />
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="flex flex-col min-h-screen">
        {/* Force the header to show in scrolled state with dark background */}
        <div className="bg-accent">
          <Header isAdminPage={true} />
        </div>
        
        {/* Admin Login with Parallax Background */}
        <div className="relative flex-grow flex items-center justify-center py-32">
          {/* Background Image with parallax effect */}
          <div 
            ref={parallaxRef}
            className="absolute inset-0 z-0 overflow-hidden"
          >
            <img 
              src={LargeLogo}
              alt="Background Logo"
              className="absolute w-full h-full object-cover opacity-10"
              style={{
                transform: 'scale(1.1)',
                objectFit: 'contain'
              }}
            />
          </div>
          
          {/* Dark overlay for better visibility of white text in header */}
          <div className="absolute top-0 inset-x-0 h-32 bg-gradient-to-b from-accent to-transparent z-0"></div>
          
          {/* Admin Login Form */}
          <div className="z-10 px-4 w-full max-w-md">
            <AdminLogin onLogin={handleLogin} />
          </div>
        </div>
        
        <Footer isAdminPage={true} />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header isAdminPage={true} />
      
      <div className="pt-24 flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 mb-6 bg-white shadow-sm rounded-lg flex justify-between items-center">
          <h1 className="text-2xl font-heading font-bold text-text">Admin Panel</h1>
          <button 
            onClick={handleLogout}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
          >
            Logout
          </button>
        </div>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 mb-12">
          <div className="mb-8">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('products')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'products'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Products
                </button>
                <button
                  onClick={() => setActiveTab('categories')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'categories'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Categories
                </button>
              </nav>
            </div>
          </div>

          {activeTab === 'products' && (
            <ProductManager products={products} setProducts={setProducts} />
          )}

          {activeTab === 'categories' && (
            <CategoryManager />
          )}
        </main>
      </div>
      
      <Footer isAdminPage={true} />
    </div>
  );
};

export default AdminPanel; 